import React from "react";
import GlobalStyles, { CommonClasses } from "../global/GlobalStyle";
import {
  MdSecurity,
  MdTrendingUp,
  MdPeople,
  MdVerifiedUser,
  MdFlag,
  MdRemoveRedEye,
} from "react-icons/md";

const About = () => {
  const MissionIcon = MdFlag as React.FC<React.SVGProps<SVGSVGElement>>; // 🎯 Mission
  const VisionIcon = MdRemoveRedEye as React.FC<React.SVGProps<SVGSVGElement>>; // 👁️ Vision
  const SecurityIcon = MdSecurity as React.FC<React.SVGProps<SVGSVGElement>>; // 🔒 Security
  const TrendIcon = MdTrendingUp as React.FC<React.SVGProps<SVGSVGElement>>; // 📈 Growth
  const PeopleIcon = MdPeople as React.FC<React.SVGProps<SVGSVGElement>>; // 👥 Team
  const TrustIcon = MdVerifiedUser as React.FC<React.SVGProps<SVGSVGElement>>; //

  const values = [
    {
      icon: SecurityIcon,
      title: "Security First",
      description:
        "We prioritize the safety and security of our clients' assets above all else.",
    },
    {
      icon: TrendIcon,
      title: "Innovation",
      description:
        "Constantly evolving our strategies to adapt to market changes and opportunities.",
    },
    {
      icon: PeopleIcon,
      title: "Client Success",
      description:
        "Your financial success is our ultimate measure of achievement.",
    },
    {
      icon: TrustIcon,
      title: "Transparency",
      description:
        "Clear, honest communication about our processes and your investments.",
    },
  ];

  return (
    <div className={GlobalStyles.container}>
      {/* Header */}
      <div className="text-center mb-12 py-16">
        <h1
          className={`${CommonClasses.textXl} font-bold ${CommonClasses.textGrayStrong} mb-4`}
        >
          About OFS Security Ledger
        </h1>
        <p
          className={`${CommonClasses.textLg} ${CommonClasses.textGray} max-w-3xl mx-auto`}
        >
          Pioneering the future of cryptocurrency asset protection with
          intelligent market solutions and unparalleled security.
        </p>
      </div>

      {/* Mission & Vision */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
        <div className={`${GlobalStyles.sectionWrap} text-center`}>
          <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
            <MissionIcon className="w-8 h-8 text-white" />
          </div>
          <h2
            className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong} mb-4`}
          >
            Our Mission
          </h2>
          <p className={`${CommonClasses.textGray}`}>
            To provide cryptocurrency investors with sophisticated protection
            mechanisms that safeguard their assets during market volatility
            while optimizing growth during favorable conditions. We believe in
            making advanced financial protection accessible to all digital asset
            holders.
          </p>
        </div>

        <div className={`${GlobalStyles.sectionWrap} text-center`}>
          <div className="w-16 h-16 bg-secondary rounded-full flex items-center justify-center mx-auto mb-4">
            <VisionIcon className="w-8 h-8 text-white" />
          </div>
          <h2
            className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong} mb-4`}
          >
            Our Vision
          </h2>
          <p className={`${CommonClasses.textGray}`}>
            To become the global standard for cryptocurrency asset protection,
            creating a financial ecosystem where digital asset investors can
            participate in market opportunities without exposure to catastrophic
            losses.
          </p>
        </div>
      </div>

      {/* Our Story */}
      <div className={`${GlobalStyles.sectionWrap} mb-12`}>
        <h2
          className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong} mb-6 text-center`}
        >
          Our Story
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          <div>
            <p className={`${CommonClasses.textGray} mb-4`}>
              OFS Security Ledger was founded in 2020 by a team of financial
              experts, blockchain developers, and cybersecurity specialists who
              recognized the growing need for sophisticated protection in the
              volatile cryptocurrency markets.
            </p>
            <p className={`${CommonClasses.textGray} mb-4`}>
              After witnessing countless investors suffer significant losses
              during market downturns, we set out to create a solution that
              would protect digital assets while maintaining the potential for
              growth.
            </p>
            <p className={`${CommonClasses.textGray}`}>
              Today, we serve thousands of clients worldwide, protecting
              millions in digital assets with our innovative security ledger
              system.
            </p>
          </div>
          <div className="bg-gradient-to-br from-primary to-purple-600 rounded-2xl p-8 text-white text-center">
            <div className="text-3xl font-bold mb-2">$100M+</div>
            <div className="text-lg mb-4">Assets Protected</div>
            <div className="text-3xl font-bold mb-2">10,000+</div>
            <div className="text-lg mb-4">Happy Clients</div>
            <div className="text-3xl font-bold mb-2">99.9%</div>
            <div className="text-lg">Uptime Record</div>
          </div>
        </div>
      </div>

      {/* Values */}
      <div className="mb-12">
        <h2
          className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong} mb-8 text-center`}
        >
          Our Values
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {values.map((value, index) => {
            const Icon = value.icon;
            return (
              <div
                key={index}
                className={`${GlobalStyles.sectionWrap} text-center p-6`}
              >
                <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <h3
                  className={`font-semibold ${CommonClasses.textGrayStrong} mb-3`}
                >
                  {value.title}
                </h3>
                <p
                  className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}
                >
                  {value.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Technology */}
      <div className={`${GlobalStyles.sectionWrap}`}>
        <h2
          className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong} mb-6 text-center`}
        >
          Advanced Technology
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <h3
              className={`font-semibold ${CommonClasses.textGrayStrong} mb-3`}
            >
              Intelligent Market Analysis
            </h3>
            <p
              className={`${CommonClasses.textSm} ${CommonClasses.textGray} mb-4`}
            >
              Our proprietary algorithms continuously monitor market conditions,
              identifying potential downturns before they impact your portfolio.
            </p>
            <ul
              className={`${CommonClasses.textSm} ${CommonClasses.textGray} space-y-2`}
            >
              <li>• Real-time market sentiment analysis</li>
              <li>• Predictive volatility modeling</li>
              <li>• Automated protection triggers</li>
              <li>• Multi-exchange data aggregation</li>
            </ul>
          </div>
          <div>
            <h3
              className={`font-semibold ${CommonClasses.textGrayStrong} mb-3`}
            >
              Military-Grade Security
            </h3>
            <p
              className={`${CommonClasses.textSm} ${CommonClasses.textGray} mb-4`}
            >
              We employ the highest security standards to protect your assets
              and data.
            </p>
            <ul
              className={`${CommonClasses.textSm} ${CommonClasses.textGray} space-y-2`}
            >
              <li>• 256-bit end-to-end encryption</li>
              <li>• Multi-signature wallet technology</li>
              <li>• Cold storage for majority of assets</li>
              <li>• Regular security audits and penetration testing</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
